import React, { Dispatch, SetStateAction, useState } from 'react';
import styled from '@emotion/styled';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { PricingPlanCycle, PricingPlanType } from '@atlassian/cssxp-ccp-types';
import { PricingPlanStatus } from '@atlassian/cssxp-typescommon';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { CustomPanel } from '@css-xp/base';
import { State } from '../../../../store/types';
import { getTimestampFromLinkedProductsOrApps } from '../../../../store/utils/get-timestamp-from-linked-products-apps';
import Badge from '@atlaskit/badge';
import { v4 as generateUuid } from 'uuid';
import { xcss, Box, Flex, Text } from '@atlaskit/primitives';
import { getDefaultOffering } from '../../../add-new-products/src/components/add-new-products-modal';
import { RemovableTag as Tag } from '@atlaskit/tag';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';

import {
  isProductBelongsToJiraFamily,
  isAddOns,
  JSM_PREMIUM_OFFERING_ID,
  ProductRelationShipType,
  getProductsSearchableRelationshipTypes,
} from '@css-xp/ppc-utils';
import Button, { LoadingButton } from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { token } from '@atlaskit/tokens';
import { useCloudSiteOrOrgNameMapStore, useStore } from '../../../../store/store';
import { N500 } from '@atlaskit/theme/colors';
import { ProgressIndicator } from '@atlaskit/progress-indicator';
import { ProductsTableOrderFlow } from '../../../add-new-products/src/components/products-table';
import { AddVendorPromotionTable } from '../../../add-new-products/src/components/add-vendor-promotion-table';
import { AppPromoCodeDetails, AvailableAppForProduct } from '@css-xp/ppc-checkout/src/types';
import { PageInfo } from '@css-xp/graphtypes';
import { Field } from '@atlaskit/form';
import { fetchSearchBasedAppsForProductsInPPCQuery } from '@css-xp/ppc-checkout/src/queries/sxp-queries/fetch-seach-based-apps';
import { AppOffering, Currency } from '@css-xp/graphtypes/src/generated-types-sxp';
import {
  APP_PAGINATION_SIZE,
  JIRA_RELATIONSHIP_OFFERING_KEY,
} from '@css-xp/ppc-checkout/src/hooks/use-process-line-items-for-order-intent-estimate';
import { useLazyQuery } from 'apollo-client-ppc';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { CatalogueContextItems } from '../../../add-new-products/src/types';
import { useAddNewProductStore } from '../../../../store';
import { CartProductSchema, LinkedEntitlementRelationShipType, OperationType, ProductStatus } from '../../../../types';
import { isNewEntitlement } from '../../../../store/utils';
import { UpdateExistingEntitlementWrapper } from '../../../billing-profile/src/update-existing-entitlement-wrapper';
import { SelectType } from '../../../billing-profile/src/types';
import { AddNewProductsWrapper } from '../../../add-new-products';
import { getOfferingAndPricingPlanMapForStore } from '../../../../store/utils/get-offerings-and-pricing-plan-map-for-store';
import { getPricingPlanFilterOptionsForNativeCatalog } from '../../../../store/utils/get-catalog-pricing-plan-filters';

export const ModalFooterWrapper = styled.div`
  width: 100%;
`;

export const ModalHeaderWrapper = styled.div`
  width: 100%;
`;

export const ModalTitleWrapper = styled.div`
  width: 100%;
`;

const ProductTableStyles = styled.div`
  max-width: 100%;
  max-height: 400px;
  .products-table {
    max-width: 100%;
    padding-bottom: 0px;
  }
`;

const StyledHeader = styled.div`
  padding-left: ${token('space.400')};
`;

const StyledCustomPanel = styled.div`
  padding-top: ${token('space.200')};
`;

const styledSelectedAppContainer = xcss({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'space.075',
});

type AvailableAppsForProduct = {
  edges?: AvailableAppForProduct[];
  nodes?: AvailableAppForProduct[];
  pageInfo: PageInfo;
  totalCount?: number;
};

interface NewAppPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProductDetails?: {
    product: any;
    cloudSiteId?: string;
    cloudSiteUrl?: string;
    onAddToCart: (OrderItems: CatalogueContextItems) => void;
    sandboxCloudSiteParentProductIds?: string[];
  } | null;
  setSelectedProductDetails: Dispatch<
    SetStateAction<{
      product: any;
      cloudSiteId?: string;
      cloudSiteUrl?: string;
      onAddToCart: (OrderItems: CatalogueContextItems) => void;
      sandboxCloudSiteParentProductIds?: string[];
    } | null>
  >;
  customer?: { name: string; ids: string[] } | null;
  invoiceGroupId?: string;
  isBillingProfileDetailsLoading?: boolean;
  billingProfileError?: any;
  refetchBillingProfileDetails?: any;
  fetchEntitlementListError?: any;
  isFetchEntitlementListLoading?: boolean;
  refetchEntitlementsResponse?: any;
  externalProductsList?: any;
  isExternalProductListLoading?: boolean;
  showProductListForAppSelection?: boolean;
}

interface User {
  email: string;
  error?: string | undefined;
}

enum SCREEN_NAME {
  PRODUCT_LIST = 'PRODUCT_LIST',
  APPS_LIST = 'APPS_LIST',
  SELECTED_APPS = 'SELECTED_APPS',
}

enum FILTER_OPTIONS {
  INCLUDE_PROPERTIES = 'includeProperties',
}

enum INCLUDE_PROPERTIES {
  VENDOR_NAME = 'vendorName',
}

export type AppPromotionIdsProps = {
  productId: string;
  promotionId: string | undefined;
};

export type AppPromoCodeDetailsProps = {
  productId: string;
  promotionDetails: AppPromoCodeDetails | undefined;
};

const transformCatalogDataToCart = ({
  parentsProductDetails,
  selectedAppDetails,
  appPromotionIdsDetails,
  isJPDasJiraFamilyEnabled,
  appsInStore,
  productsInStore,
}: {
  parentsProductDetails: CartProductSchema | undefined;
  selectedAppDetails: any;
  appPromotionIdsDetails: Map<string, AppPromoCodeDetails | undefined>;
  isJPDasJiraFamilyEnabled: boolean;
  appsInStore: State['linkedEntitlements'];
  productsInStore: State['products'];
}) => {
  const transformedAppsDetails = selectedAppDetails?.map((appDetail: any) => {
    const promotionDetails = appPromotionIdsDetails?.get(appDetail?.id);
    const isJiraFamilyProduct = isProductBelongsToJiraFamily(parentsProductDetails?.productId, {
      isJPDAsJiraFamilyEnabled: isJPDasJiraFamilyEnabled,
    });
    const isParentNew = isNewEntitlement({ operationType: parentsProductDetails?.operationType });
    const parentEntitlementId = parentsProductDetails?.id;
    const relationshipType = appDetail?.isAddOn
      ? LinkedEntitlementRelationShipType.ADD_ON
      : LinkedEntitlementRelationShipType.APP;

    return {
      id: generateUuid(),
      name: appDetail?.name,
      logoId: appDetail?.logoId,
      billingCycle: parentsProductDetails?.billingCycle,
      containerOrProductId: isJiraFamilyProduct ? parentsProductDetails?.containerId ?? '' : parentsProductDetails?.id,
      activeUsers: parentsProductDetails?.activeUsers ?? '',
      customerContacts: [],
      productId: appDetail?.id,
      pricingPlanId: appDetail?.pricingPlanKey,
      offeringId: appDetail?.offeringId,
      offeringName: appDetail?.offeringName,
      operationType: OperationType.NEW,
      productStatuses: [ProductStatus.NEW],
      trialLengthInDays: appDetail?.trialLengthInDays,
      promotions: promotionDetails ? [promotionDetails] : [],
      parentEntitlementId: isParentNew ? '' : parentsProductDetails?.id,
      nextBillingTimestamp: getTimestampFromLinkedProductsOrApps({
        parentEntitlementId: parentEntitlementId as string,
        products: productsInStore,
        linkedEntitlements: appsInStore,
      }),
      inTrial: true,
      hasAllData: true,
      relationshipType,
    };
  });
  return transformedAppsDetails;
};

const getDefaultOfferingAndPricingPlanDetails = ({
  checkedProductsList,
  sandboxCloudSiteParentProductIds,
  isJPDAsJiraFamilyEnabled,
  isJSMStandardOfferingEnabled,
  isCompassPremiumEnabled,
  isLoomEnabled,
  isJPDPremiumOfferingEnabled,
}: {
  checkedProductsList: any;
  sandboxCloudSiteParentProductIds: string[];
  isJPDAsJiraFamilyEnabled?: boolean;
  isJSMStandardOfferingEnabled?: boolean;
  isCompassPremiumEnabled?: boolean;
  isLoomEnabled?: boolean;
  isJPDPremiumOfferingEnabled?: boolean;
}) => {
  return checkedProductsList.map((lineItem: any) => {
    const offering = getDefaultOffering({
      lineItem,
      sandboxCloudSiteParentProductIds,
      isJPDAsJiraFamilyEnabled,
      isJSMStandardOfferingEnabled,
      isCompassPremiumEnabled,
      isLoomEnabled,
      isJPDPremiumOfferingEnabled,
    });

    /**
     * Retrieve and send the default and commercial pricing plans from here.
     * The correct pricing plan based on the selected destination bp is determined when forming the OMS payload.
     */

    const availablePricingPlans = lineItem?.offering?.find((item: any) => item.id === offering?.value)
      ?.availablePricingPlans;
    const defaultCommercialPricingPlan = availablePricingPlans?.find(
      (plan: { pricingPlanType: string; type: string; status: string }) =>
        plan.pricingPlanType === 'DEFAULT_PRICING' &&
        plan.type === PricingPlanType.COMMERCIAL &&
        plan.status === PricingPlanStatus.ACTIVE
    );
    const pricingPlanId = defaultCommercialPricingPlan?.id;

    return {
      ...lineItem,
      offeringId: offering?.value,
      offeringName: offering?.label,
      pricingPlanKey: pricingPlanId ?? '',
    };
  });
};

export const NewAppPurchaseModal: React.FC<NewAppPurchaseModalProps> = ({
  isOpen,
  onClose,
  selectedProductDetails,
  setSelectedProductDetails,
  customer,
  invoiceGroupId,
  isBillingProfileDetailsLoading = false,
  billingProfileError,
  refetchBillingProfileDetails,
  fetchEntitlementListError,
  isFetchEntitlementListLoading = false,
  refetchEntitlementsResponse,
  externalProductsList,
  isExternalProductListLoading = false,
  showProductListForAppSelection = false,
}) => {
  const [appPromotionIdsDetails, setAppPromotionIdsDetails] = useState<AppPromoCodeDetailsProps>({
    productId: '',
    promotionDetails: undefined,
  });

  const [appsPromotionsIdsDetailsMap, setAppsPromotionsIdsDetailsMap] = React.useState<
    Map<string, AppPromoCodeDetails | undefined>
  >(new Map());

  const [{ linkedEntitlements, products, featureFlags }, actions] = useStore();

  const isMultiCurrencyEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES] ?? false;
  const isStatuspageEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_STATUSPAGE] ?? false;
  const isAppificationEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_APPIFICATION] ?? false;
  const isAddOnsPhase2Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2] ?? false;
  const isJPDPremiumOfferingEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_JPD_PREMIUM_OFFERING] ?? false;
  const isCompassPremiumEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_COMPASS_PREMIUM] ?? false;
  const isJSMStandardOfferingEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_JSM_OFFERING_CHANGE] ?? false;
  const isJPDAsJiraFamilyEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY] ?? false;
  const isLoomEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_STANDALONE_LOOM_FOR_UOF] ?? false;

  const selectedProductId = selectedProductDetails?.product?.productId;
  /**
   * We are passing productOfferingIds because JIRA apps are linked jira relationship offering container id and Add-ons are linked
   * to selected product offering id For example JSM premium offering id. In this case we have send both offering ids
   * Here is the doc for more details https://hello.atlassian.net/wiki/spaces/COMITFO/pages/2547412180/Parent+product+relationship+with+app+and+add-ons
   */
  const productSearchableRelationshipTypes = getProductsSearchableRelationshipTypes(selectedProductId);
  const isAddOnSearchable = productSearchableRelationshipTypes.includes(ProductRelationShipType.ADD_ON);
  const isAppSearchable = productSearchableRelationshipTypes.includes(ProductRelationShipType.APP);

  const isParentProductEligibleForAddOnsOrder = JSM_PREMIUM_OFFERING_ID === selectedProductDetails?.product?.offeringId;

  const [selectedProgressIndex, setSelectedProgressIndex] = useState<string>(SCREEN_NAME.PRODUCT_LIST);
  const [appDetails, setAppDetails] = React.useState<any>([]);
  const [appCursor, setAppCursor] = React.useState<string>('');
  const [checkedItemMap, setCheckedItemMap] = React.useState<Record<string, SelectType>>({});
  const [singleSelectedProduct, setSingleSelectedProduct] = React.useState<CartProductSchema | null>(null);
  const selectedProductOrContainerId = isProductBelongsToJiraFamily(selectedProductDetails?.product?.productId, {
    isJPDAsJiraFamilyEnabled: isJPDAsJiraFamilyEnabled,
  })
    ? selectedProductDetails?.product?.containerId
    : selectedProductDetails?.product?.id;

  const { addNewProductToCart } = useAddNewProductStore();

  const [selectedLinkedEntitlements, setSelectedLinkedEntitlements] = React.useState<any>([]);

  const [checkedProductsList, setCheckedProductsList] = React.useState<PartnerProductCatalogueItem[]>([]);
  const [availableProductsApps, setAvailableProductsApps] = React.useState<any>();
  const [appSearchText, setAppSearchText] = React.useState<string>('');
  const [appSearchInputValue, setAppSearchInputValue] = React.useState<string>('');
  const sxpClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.SXP), []);

  const [screenValues, setScreenValues] = useState<string[]>([]);
  const [fetchAvailableAppData, { data: availableProductAppsList, loading: appsDataLoading }] = useLazyQuery(
    fetchSearchBasedAppsForProductsInPPCQuery,
    {
      client: sxpClient as any,
    }
  );

  const [cloudSiteOrOrgNameMapData] = useCloudSiteOrOrgNameMapStore();

  const validOfferingIds = React.useMemo(() => {
    if (
      isProductBelongsToJiraFamily(selectedProductDetails?.product?.productId, {
        isJPDAsJiraFamilyEnabled: isJPDAsJiraFamilyEnabled,
      })
    ) {
      /**
       * We are passing productOfferingIds because JIRA apps are linked jira relationship offering container id and Add-ons are linked
       * to selected product offering id For example JSM premium offering id. In this case we have send both offering ids
       * Here is the doc for more details https://hello.atlassian.net/wiki/spaces/COMITFO/pages/2547412180/Parent+product+relationship+with+app+and+add-ons
       */
      return isAddOnsPhase2Enabled && isAddOnSearchable
        ? [JIRA_RELATIONSHIP_OFFERING_KEY, JSM_PREMIUM_OFFERING_ID]
        : [JIRA_RELATIONSHIP_OFFERING_KEY];
    }
    return [selectedProductDetails?.product?.offeringId];
  }, [selectedProductDetails?.product]);

  const parentContainerOrProductId = React.useMemo(() => {
    const isJiraFamilyProduct = isProductBelongsToJiraFamily(selectedProductDetails?.product?.productId, {
      isJPDAsJiraFamilyEnabled: isJPDAsJiraFamilyEnabled,
    });
    return isJiraFamilyProduct
      ? (selectedProductDetails?.product?.containerId as any) ?? ''
      : (selectedProductDetails?.product?.productId as any);
  }, [selectedProductDetails?.product]);

  React.useEffect(() => {
    // here we need to auto populate the addons without search keyword
    if (appSearchText || isStatuspage(selectedProductId)) {
      fetchAvailableAppData({
        variables: {
          searchText: appSearchText,
          ids: validOfferingIds,
          pageParams: {
            size: APP_PAGINATION_SIZE,
            cursor: appCursor,
          },
          pricingPlanFilterOptions: getPricingPlanFilterOptionsForNativeCatalog(isMultiCurrencyEnabled),
          fetchPartnerPromotionStatus: true,
          filterOptions: [
            {
              key: FILTER_OPTIONS.INCLUDE_PROPERTIES,
              values: [INCLUDE_PROPERTIES.VENDOR_NAME],
            },
            ...(isStatuspageEnabled && isStatuspage(selectedProductId)
              ? [
                  {
                    key: 'isStatuspageAddonSearch',
                    value: 'true',
                  },
                ]
              : []),
            ...(isAddOnsPhase2Enabled && isAddOnSearchable
              ? [
                  {
                    key: 'isAddOnsSearchEnabled',
                    value: 'true',
                  },
                ]
              : []),
          ],
        },
      });
    }
  }, [
    selectedProductDetails?.product,
    appSearchText,
    isMultiCurrencyEnabled,
    appCursor,
    selectedProductDetails?.product?.offeringId,
    selectedProductId,
  ]);

  const availableProductAppsData = React.useMemo(() => {
    return defaultTo(get(availableProductAppsList, 'availableAppsOnSearchBased'), []);
  }, [availableProductAppsList]);

  React.useEffect(() => {
    const checkedProduct = Object.keys(checkedItemMap).find((key) => checkedItemMap[key] === SelectType.ENTITLEMENT);
    if (checkedProduct) setSingleSelectedProduct(products[checkedProduct]);
  }, [checkedItemMap]);

  React.useEffect(() => {
    if (showProductListForAppSelection) {
      setSelectedProgressIndex(SCREEN_NAME.PRODUCT_LIST);
      setScreenValues([SCREEN_NAME.PRODUCT_LIST, SCREEN_NAME.APPS_LIST, SCREEN_NAME.SELECTED_APPS]);
    } else {
      setSelectedProgressIndex(SCREEN_NAME.APPS_LIST);
      setScreenValues([SCREEN_NAME.APPS_LIST, SCREEN_NAME.SELECTED_APPS]);
    }
  }, [showProductListForAppSelection]);

  const handleAddNewAppsClick = ({
    product,
    cloudSiteId,
    cloudSiteUrl,
    onAddToCart,
    sandboxCloudSiteParentProductIds,
  }: {
    product: any;
    cloudSiteId?: string;
    cloudSiteUrl?: string;
    onAddToCart: (OrderItems: CatalogueContextItems) => void;
    sandboxCloudSiteParentProductIds: string[];
  }) => {
    setSelectedProductDetails({
      product,
      cloudSiteId,
      cloudSiteUrl,
      onAddToCart,
      sandboxCloudSiteParentProductIds,
    });
    setSelectedProgressIndex(SCREEN_NAME.APPS_LIST);
  };

  React.useEffect(() => {
    if (availableProductAppsData?.nodes?.length) {
      const allAvailableProductApps = [
        ...(appCursor && defaultTo(availableProductsApps?.nodes as any, [])),
        ...availableProductAppsData?.nodes,
      ];

      // Add isApp and isEnabledForOrder properties to each object
      const updatedProductApps = allAvailableProductApps.map((item) => {
        const isAddOnEligibleToOrder = isAddOns(item?.id);
        const isApp = item?.isApp;
        const isAddOn = item?.isAddOn;
        const isStatusPageAddOn = isAddOn && isStatuspage(item?.productKey);
        /**
         * we are converting productId = offeringId for statuspage addons and saving in store.
         * we are making this change because all statuspage addons have same product id
         * here is doc for more info https://hello.atlassian.net/wiki/spaces/~70121d05ec16fa6f7444db10a305ee7e0df3c/pages/5047030716/Statuspage+addons+support+in+UOF
         */
        const productKey = isStatusPageAddOn ? item?.offeringId : item?.productKey;
        return {
          ...item,
          isApp,
          isAddOn,
          productKey,
          id: productKey,
          name: isStatusPageAddOn ? `${item?.offeringName}` : item?.name, // Displaying offering name for statuspage add-ons
          isEnabledForOrder: isAddOnEligibleToOrder ? isParentProductEligibleForAddOnsOrder : true, // Add-ons are enabled only for JSM premium offering
        };
      });

      // to make unique array
      const uniqueAvailableProductApps = [...new Map(updatedProductApps.map((item) => [item['id'], item])).values()];

      setAvailableProductsApps({
        ...availableProductAppsData,
        nodes: [...uniqueAvailableProductApps],
      });
    } else if (availableProductAppsData?.nodes) {
      // setting empty array when apps not present in the data
      setAvailableProductsApps({
        ...availableProductAppsData,
        nodes: [],
      });
      setAppCursor('');
      setAppSearchText('');
    }
  }, [availableProductAppsData]);

  React.useEffect(() => {
    if (appPromotionIdsDetails?.productId) {
      setAppsPromotionsIdsDetailsMap((prev) => {
        const newAppsPromotionsIdsDetailsMap = new Map(prev);
        newAppsPromotionsIdsDetailsMap.set(appPromotionIdsDetails?.productId, appPromotionIdsDetails?.promotionDetails);
        return newAppsPromotionsIdsDetailsMap;
      });
    }
  }, [appPromotionIdsDetails]);

  React.useEffect(() => {
    // updates the price so that we can show total price on page
    appDetails.forEach((app: AppOffering) => {
      for (let appItem of defaultTo(selectedLinkedEntitlements[selectedProductDetails?.product?.productId], [])) {
        if (get(appItem, 'id') === get(app, 'id')) {
          appItem = { ...appItem, price: defaultTo(get(app, 'price'), 0), maxUserCount: 1 };
        }
      }
    });
    setSelectedLinkedEntitlements({ ...selectedLinkedEntitlements });
  }, [appDetails]);

  const handlePrev = () => {
    if (selectedProgressIndex === SCREEN_NAME.SELECTED_APPS) {
      setSelectedProgressIndex(SCREEN_NAME.APPS_LIST);
    }
    if (selectedProgressIndex === SCREEN_NAME.APPS_LIST) {
      setSelectedProgressIndex(SCREEN_NAME.PRODUCT_LIST);
    }
  };

  const handleSubmit = async () => {
    if (selectedProgressIndex === SCREEN_NAME.PRODUCT_LIST) {
      setSelectedProgressIndex(SCREEN_NAME.APPS_LIST);
    }

    if (selectedProgressIndex === SCREEN_NAME.APPS_LIST) {
      setSelectedProgressIndex(SCREEN_NAME.SELECTED_APPS);
      const offeringsAndPricingPlanDetails = getOfferingAndPricingPlanMapForStore(checkedProductsList);
      actions.updateOfferingAndPricingPlans(offeringsAndPricingPlanDetails);
    }

    if (selectedProgressIndex === SCREEN_NAME.SELECTED_APPS) {
      const checkedProductOfferingAndPlanDetails = getDefaultOfferingAndPricingPlanDetails({
        checkedProductsList,
        sandboxCloudSiteParentProductIds: selectedProductDetails?.sandboxCloudSiteParentProductIds ?? ([] as string[]),
        isJPDAsJiraFamilyEnabled,
        isJSMStandardOfferingEnabled,
        isCompassPremiumEnabled,
        isLoomEnabled,
        isJPDPremiumOfferingEnabled,
      });

      const processedLineItems = transformCatalogDataToCart({
        parentsProductDetails: selectedProductDetails?.product,
        selectedAppDetails: checkedProductOfferingAndPlanDetails,
        appPromotionIdsDetails: appsPromotionsIdsDetailsMap,
        isJPDasJiraFamilyEnabled: !!isJPDAsJiraFamilyEnabled,
        appsInStore: linkedEntitlements,
        productsInStore: products,
      });

      setSelectedProductDetails(null);
      setSingleSelectedProduct(null);
      setCheckedItemMap({});
      addNewProductToCart([], processedLineItems);
      onClose();
      setSelectedProgressIndex(showProductListForAppSelection ? SCREEN_NAME.PRODUCT_LIST : SCREEN_NAME.APPS_LIST);
      setCheckedProductsList([]);
      setAppPromotionIdsDetails({ productId: '', promotionDetails: undefined });
      setAppSearchInputValue('');
      setAppSearchText?.('');
      setAppCursor('');
      setAvailableProductsApps({});
    }
  };

  const handleOnClose = () => {
    onClose();
    setSelectedProductDetails(null);
    setSingleSelectedProduct(null);
    setCheckedItemMap({});
    setSelectedProgressIndex(showProductListForAppSelection ? SCREEN_NAME.PRODUCT_LIST : SCREEN_NAME.APPS_LIST);
    setCheckedProductsList([]);
    setAppPromotionIdsDetails({ productId: '', promotionDetails: undefined });
    setAppSearchInputValue('');
    setAppSearchText?.('');
    setAppCursor('');
    setAvailableProductsApps({});
  };

  const handleSearchInputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setAppSearchInputValue(value);
    if (!value) {
      setAppSearchText?.('');
      setAvailableProductsApps({
        ...availableProductAppsData,
        nodes: [],
      });
    }
  };

  const getHeader = (appsCount: number) => {
    return (
      <Flex alignItems="center" gap="space.150">
        <StyledHeader>
          <Text align="end">
            <strong>Selected apps</strong>
          </Text>
        </StyledHeader>
        <Badge>{appsCount}</Badge>
        <Button
          onClick={() => {
            setCheckedProductsList([]);
          }}
        >
          Clear all
        </Button>
      </Flex>
    );
  };

  const headerContent = React.useMemo(() => {
    if (selectedProgressIndex === SCREEN_NAME.PRODUCT_LIST) {
      return {
        title: 'First, select a subscription',
        subTitle: 'Look for a subscription or a site to add to your order.',
      };
    }

    const productDetails = `${selectedProductDetails?.product?.name} ${
      selectedProductDetails?.cloudSiteUrl ? ` - ${selectedProductDetails.cloudSiteUrl}` : ''
    }`;
    if (isAddOnsPhase2Enabled && isAddOnSearchable && isAppSearchable) {
      return {
        title: `Add${isAppificationEnabled ? ' Marketplace' : ''} apps or add-ons`,
        subTitle: `Select one or more ${isAppificationEnabled ? 'items' : 'apps/add-ons'} for ${productDetails}`,
      };
    } else if (isAddOnSearchable && (isAddOnsPhase2Enabled || isStatuspageEnabled)) {
      return {
        title: 'Add add-ons',
        subTitle: `Select at least one addon for ${productDetails}`,
      };
    }
    return {
      title: `Add${isAppificationEnabled ? ' Marketplace' : ''} apps`,
      subTitle: `Select at least one app for ${productDetails}`,
    };
  }, [
    selectedProgressIndex,
    isAppificationEnabled,
    isAddOnSearchable,
    isAddOnsPhase2Enabled,
    isStatuspageEnabled,
    selectedProductDetails,
  ]);

  const searchPlaceHolderText = React.useMemo(() => {
    if (isAddOnsPhase2Enabled && isAddOnSearchable && isAppSearchable) {
      return `Search${isAppificationEnabled ? ' Marketplace' : ''} apps or add-ons`;
    } else if (isAddOnSearchable && (isAddOnsPhase2Enabled || isStatuspageEnabled)) {
      return `Search add-ons`;
    }
    return `Search${isAppificationEnabled ? ' Marketplace' : ''} apps`;
  }, [isAddOnSearchable, isAppificationEnabled, isAddOnsPhase2Enabled, isAppSearchable, isStatuspageEnabled]);

  return (
    <ModalTransition>
      {isOpen && (
        <Modal testId="transfer-entitlements-modal" height={720} width={752}>
          <ModalHeader>
            <ModalHeaderWrapper>
              <ModalTitleWrapper className="flex align-center justify-space-between">
                <ModalTitle>{headerContent.title}</ModalTitle>
                <Button appearance="subtle" onClick={handleOnClose} testId="new-app-purchase-close-modal-button">
                  <CrossIcon size="small" label="Close Modal" primaryColor={token('color.text.subtle', N500)} />
                </Button>
              </ModalTitleWrapper>
              <Text size="small" color="color.text.subtlest">
                {headerContent.subTitle}
              </Text>
            </ModalHeaderWrapper>
          </ModalHeader>
          <ModalBody>
            <div className="new-app-purchase-body">
              {selectedProgressIndex === SCREEN_NAME.PRODUCT_LIST && customer && invoiceGroupId && (
                <UpdateExistingEntitlementWrapper
                  customerIds={customer.ids}
                  checkedItemMap={checkedItemMap}
                  setCheckedItemMap={setCheckedItemMap}
                  alreadyCheckedItemMap={{}}
                  billingProfileError={billingProfileError}
                  refetchBillingProfileDetails={refetchBillingProfileDetails}
                  isBillingProfileDetailsLoading={isBillingProfileDetailsLoading}
                  fetchEntitlementListError={fetchEntitlementListError}
                  refetchEntitlementsResponse={refetchEntitlementsResponse}
                  isFetchEntitlementListLoading={isFetchEntitlementListLoading}
                  externalProductsList={externalProductsList}
                  isExternalProductListLoading={isExternalProductListLoading}
                  cloudSiteOrOrgNameMapData={cloudSiteOrOrgNameMapData}
                  isSingleProductSelection={true}
                />
              )}
              {selectedProgressIndex === SCREEN_NAME.APPS_LIST && (
                <>
                  {!isStatuspage(selectedProductId) && (
                    <Field name={'apps.' + get(selectedProductDetails?.product, 'id')} defaultValue={appDetails}>
                      {() => (
                        <div className="product-app-field">
                          <Textfield
                            role="search"
                            className="search-app-field"
                            aria-label={'search-new-product'}
                            placeholder={searchPlaceHolderText}
                            testId="product-app-search"
                            elemBeforeInput={<SearchIcon label="" size="small" />}
                            value={appSearchInputValue}
                            onChange={handleSearchInputOnChange}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                setAppSearchText?.(e.currentTarget.value);
                              }
                            }}
                          />
                        </div>
                      )}
                    </Field>
                  )}
                  {checkedProductsList?.length > 0 && (
                    <StyledCustomPanel>
                      <CustomPanel
                        expandedHeader={getHeader(checkedProductsList?.length)}
                        collapsedHeader={getHeader(checkedProductsList?.length)}
                        version={2}
                        isExpanded
                      >
                        <Box padding="space.250" xcss={styledSelectedAppContainer}>
                          {checkedProductsList?.map((app: any) => (
                            <Box key={app.id}>
                              <Tag
                                text={app.name}
                                isRemovable={true}
                                onAfterRemoveAction={() => {
                                  setCheckedProductsList(checkedProductsList?.filter((item) => item?.id !== app.id));
                                }}
                              />
                            </Box>
                          ))}
                        </Box>
                      </CustomPanel>
                    </StyledCustomPanel>
                  )}
                  <ProductTableStyles>
                    <ProductsTableOrderFlow
                      availableProductsApps={availableProductsApps}
                      appsDataLoading={appsDataLoading}
                      allowedParentProductIdsForAppsOrder={[parentContainerOrProductId]}
                      showCheckbox={true}
                      checkedProductsList={checkedProductsList}
                      setCheckedProductsList={setCheckedProductsList}
                      parentContainerOrProductId={parentContainerOrProductId}
                      quoteItemsInCart={linkedEntitlements[selectedProductOrContainerId] as any}
                      isDesignIteration1Enabled={true}
                      isAddOnsSearchEnabled={isAddOnSearchable && (isAddOnsPhase2Enabled || isStatuspageEnabled)}
                      isAppsSearchEnabled={isAppSearchable}
                      isAppificationEnabled={isAppificationEnabled}
                    />
                  </ProductTableStyles>
                </>
              )}
              {selectedProgressIndex === SCREEN_NAME.SELECTED_APPS && (
                <ProductTableStyles>
                  <AddVendorPromotionTable
                    appsList={checkedProductsList}
                    setAppPromotionIdsDetails={setAppPromotionIdsDetails}
                    setCheckedProductsList={setCheckedProductsList}
                  />
                </ProductTableStyles>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <ModalFooterWrapper className="flex align-center justify-space-between">
              <Button
                isDisabled={
                  selectedProgressIndex === SCREEN_NAME.PRODUCT_LIST ||
                  (!showProductListForAppSelection && selectedProgressIndex === SCREEN_NAME.APPS_LIST)
                }
                onClick={handlePrev}
                testId="transfer-entitlements-prev-button"
              >
                Prev
              </Button>
              <ProgressIndicator
                testId="transfer-entitlements-progress-indicator"
                values={screenValues}
                selectedIndex={screenValues.indexOf(selectedProgressIndex)}
              />
              {selectedProgressIndex === SCREEN_NAME.PRODUCT_LIST && (
                <AddNewProductsWrapper
                  cloudSiteHost={singleSelectedProduct?.cloudSite || ''}
                  cloudSiteId={singleSelectedProduct?.cloudSiteId}
                  isDisabled={!singleSelectedProduct?.id || !Object.keys(checkedItemMap).length}
                  addNewProductLabel="Next"
                  hideIconBefore={true}
                  handleAddNewAppsClick={handleAddNewAppsClick}
                  product={singleSelectedProduct}
                  showLoadingIndicator={true}
                />
              )}
              {selectedProgressIndex !== SCREEN_NAME.PRODUCT_LIST && (
                <LoadingButton
                  type="submit"
                  onClick={handleSubmit}
                  isLoading={false}
                  testId="new-app-purchase-next-button"
                  appearance={selectedProgressIndex === SCREEN_NAME.APPS_LIST ? 'default' : 'primary'}
                  isDisabled={selectedProgressIndex === SCREEN_NAME.APPS_LIST && checkedProductsList?.length === 0}
                >
                  {selectedProgressIndex === SCREEN_NAME.APPS_LIST ? 'Next' : 'Add to order'}
                </LoadingButton>
              )}
            </ModalFooterWrapper>
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};
